import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ComplianceTenantSettings, Induction, InductionPassList } from '@cloudsign/shared';
import type { QuizQuestion } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { ExportButton } from '../../components/ExportButton';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, CheckboxField, EmptyRow, PageHeader, Pager, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { QuizEditor, UploadWithProgress, VideoPlayer, quizProblems } from '../../components/phase3';
import { Dialog } from '../fields-notices/Dialog';
import { useFileUrl } from '../sites/api';
import { COMPLIANCE, useGroupOptions, useSiteOptions } from './api';

const InductionList = z.array(Induction);
const LargeUpload = z.object({
  uploadId: z.uuid(),
  sizeBytes: z.number().int(),
  mime: z.string(),
});
const LIMITS = ComplianceTenantSettings.parse({});

interface Assignment {
  groupId: string;
  siteId: string | null;
}

interface Draft {
  name: string;
  body: string;
  minWatchSeconds: number;
  questions: QuizQuestion[];
  passMarkPercent: number;
  validityMonths: number;
  assignments: Assignment[];
  active: boolean;
  /** A new file uploaded in this editor. */
  newUploadId: string | null;
  newKind: 'video' | 'pdf' | null;
  newName: string;
  removeMedia: boolean;
}

function toDraft(i: Induction | null): Draft {
  return {
    name: i?.name ?? '',
    body: i?.body ?? '',
    minWatchSeconds: i?.minWatchSeconds ?? 0,
    questions: i?.questions ?? [],
    passMarkPercent: i?.passMarkPercent ?? 80,
    validityMonths: i?.validityMonths ?? 12,
    assignments: i?.assignments ?? [],
    active: i?.active ?? true,
    newUploadId: null,
    newKind: null,
    newName: '',
    removeMedia: false,
  };
}

function MediaPreview({ induction }: { induction: Induction }) {
  const url = useFileUrl(induction.mediaFileId);
  if (!induction.mediaFileId || !induction.mediaKind) return null;
  if (!url) return <p className="text-xs text-slate-600">{t('common.loading')}</p>;
  return induction.mediaKind === 'video' ? (
    <VideoPlayer src={url} title={t('w18.ind.videoTitle', { name: induction.name })} />
  ) : (
    <a className="text-sm font-medium text-brand underline" href={url} target="_blank" rel="noopener noreferrer">
      {t('w18.ind.openPdf')}
    </a>
  );
}

function Editor({ induction, onClose }: { induction: Induction | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const groups = useGroupOptions();
  const sites = useSiteOptions();
  const [d, setD] = useState<Draft>(toDraft(induction));
  const [problem, setProblem] = useState('');
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: d.name,
        body: d.body,
        minWatchSeconds: d.minWatchSeconds,
        questions: d.questions,
        passMarkPercent: d.passMarkPercent,
        validityMonths: d.validityMonths,
        assignments: d.assignments,
        ...(d.newUploadId ? { mediaUploadId: d.newUploadId } : {}),
        ...(induction ? { active: d.active, removeMedia: d.removeMedia && !d.newUploadId } : {}),
      };
      return induction
        ? api.patch(`${COMPLIANCE}/inductions/${induction.id}`, {
            body,
            schema: Induction,
          })
        : api.post(`${COMPLIANCE}/inductions`, { body, schema: Induction });
    },
    onSuccess: async () => {
      toast(t('common.saved'));
      await qc.invalidateQueries({ queryKey: ['w18', 'inductions'] });
      onClose();
    },
  });
  const errors = fieldErrors(save.error);
  const hasVideo = d.newKind ? d.newKind === 'video' : !d.removeMedia && induction?.mediaKind === 'video';
  const setAssign = (i: number, patch: Partial<Assignment>) =>
    setD({
      ...d,
      assignments: d.assignments.map((a, j) => (j === i ? { ...a, ...patch } : a)),
    });
  const submit = () => {
    setProblem('');
    if (d.name.trim() === '') return setProblem(t('w18.ind.needName'));
    if (quizProblems(d.questions).length > 0) return setProblem(t('w18.ind.fixQuiz'));
    if (d.assignments.some((a) => !a.groupId)) return setProblem(t('w18.ind.needGroup'));
    save.mutate();
  };
  return (
    <Dialog title={induction ? t('w18.ind.edit') : t('w18.ind.add')} onClose={onClose} wide>
      <div className="space-y-6">
        <TextField label={t('w18.ind.name')} required maxLength={120} value={d.name} error={errors['name']} onChange={(e) => setD({ ...d, name: e.target.value })} />
        <div>
          <label htmlFor="w18-ind-body" className="mb-1 block text-sm font-medium text-slate-800">
            {t('w18.ind.body')}
          </label>
          <textarea
            id="w18-ind-body"
            rows={5}
            className="block w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            value={d.body}
            onChange={(e) => setD({ ...d, body: e.target.value })}
            aria-describedby="w18-ind-body-hint"
          />
          <p id="w18-ind-body-hint" className="mt-1 text-xs text-slate-600">
            {t('w18.ind.bodyHint')}
          </p>
        </div>

        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-slate-900">{t('w18.ind.media')}</legend>
          {induction?.mediaFileId && !d.removeMedia && !d.newUploadId ? (
            <div className="space-y-2">
              <MediaPreview induction={induction} />
              <Button onClick={() => setD({ ...d, removeMedia: true })}>{t('w18.ind.removeMedia')}</Button>
            </div>
          ) : null}
          {d.removeMedia && !d.newUploadId ? (
            <p className="text-sm text-slate-700">
              {t('w18.ind.mediaWillBeRemoved')}{' '}
              <button type="button" className="font-medium text-brand underline" onClick={() => setD({ ...d, removeMedia: false })}>
                {t('w18.ind.undo')}
              </button>
            </p>
          ) : null}
          <UploadWithProgress
            label={induction?.mediaFileId || d.newUploadId ? t('w18.ind.replaceMedia') : t('w18.ind.uploadMedia')}
            url="/api/v1/files/uploads/large?purpose=induction_media"
            maxBytes={LIMITS.maxVideoMb * 1024 * 1024}
            accept={['video/mp4', 'application/pdf']}
            hint={t('w18.ind.mediaHint', { max: LIMITS.maxVideoMb })}
            onDone={(res, file) => {
              const parsed = LargeUpload.safeParse(res);
              if (!parsed.success) return setProblem(t('p3w.upload.failed'));
              setD({
                ...d,
                newUploadId: parsed.data.uploadId,
                newKind: parsed.data.mime === 'application/pdf' ? 'pdf' : 'video',
                newName: file.name,
                removeMedia: false,
              });
            }}
          />
          {d.newUploadId ? <p className="text-sm text-green-800">{t('w18.ind.newMedia', { name: d.newName })}</p> : null}
          {hasVideo ? (
            <TextField
              type="number"
              min={0}
              max={3600}
              className="max-w-56"
              label={t('w18.ind.minWatch')}
              hint={t('w18.ind.minWatchHint')}
              value={d.minWatchSeconds}
              error={errors['minWatchSeconds']}
              onChange={(e) =>
                setD({
                  ...d,
                  minWatchSeconds: Math.max(0, Math.min(3600, Number(e.target.value) || 0)),
                })
              }
            />
          ) : null}
        </fieldset>

        <section aria-labelledby="w18-ind-quiz" className="space-y-3">
          <h3 id="w18-ind-quiz" className="text-sm font-semibold text-slate-900">
            {t('w18.ind.quiz')}
          </h3>
          <QuizEditor value={d.questions} onChange={(questions) => setD({ ...d, questions })} passMarkPercent={d.passMarkPercent} />
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              type="number"
              min={0}
              max={100}
              label={t('w18.ind.passMark')}
              value={d.passMarkPercent}
              error={errors['passMarkPercent']}
              onChange={(e) =>
                setD({
                  ...d,
                  passMarkPercent: Math.max(0, Math.min(100, Number(e.target.value) || 0)),
                })
              }
            />
            <TextField
              type="number"
              min={1}
              max={60}
              label={t('w18.ind.validity')}
              hint={t('w18.ind.validityHint')}
              value={d.validityMonths}
              error={errors['validityMonths']}
              onChange={(e) =>
                setD({
                  ...d,
                  validityMonths: Math.max(1, Math.min(60, Number(e.target.value) || 1)),
                })
              }
            />
          </div>
        </section>

        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-slate-900">{t('w18.ind.assign')}</legend>
          <p className="text-xs text-slate-600">{t('w18.ind.assignHint')}</p>
          {d.assignments.map((a, i) => (
            <div key={i} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
              <SelectField label={t('w18.ind.group')} value={a.groupId} onChange={(e) => setAssign(i, { groupId: e.target.value })}>
                <option value="">{t('w18.ind.chooseGroup')}</option>
                {(groups.data ?? []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </SelectField>
              <SelectField label={t('w18.ind.site')} value={a.siteId ?? ''} onChange={(e) => setAssign(i, { siteId: e.target.value || null })}>
                <option value="">{t('w18.ind.everySite')}</option>
                {(sites.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </SelectField>
              <Button
                onClick={() =>
                  setD({
                    ...d,
                    assignments: d.assignments.filter((_, j) => j !== i),
                  })
                }
                aria-label={t('w18.ind.removeAssign', { n: i + 1 })}
              >
                {t('w18.ind.remove')}
              </Button>
            </div>
          ))}
          <Button
            onClick={() =>
              setD({
                ...d,
                assignments: [...d.assignments, { groupId: '', siteId: null }],
              })
            }
          >
            {t('w18.ind.addAssign')}
          </Button>
        </fieldset>

        {induction ? <CheckboxField label={t('w18.ind.active')} checked={d.active} onChange={(e) => setD({ ...d, active: e.target.checked })} /> : null}
        {induction && induction.version > 0 ? <p className="text-xs text-slate-600">{t('w18.ind.versionNote', { version: induction.version })}</p> : null}
        {problem ? <Alert>{problem}</Alert> : null}
        <ErrorLine error={save.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={save.isPending} onClick={submit}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function PassesDialog({ induction, onClose }: { induction: Induction; onClose: () => void }) {
  const [pageNo, setPageNo] = useState(1);
  const q = useQuery({
    queryKey: ['w18', 'inductions', 'passes', induction.id, pageNo],
    queryFn: () =>
      api.get(`${COMPLIANCE}/inductions/${induction.id}/passes`, {
        query: { page: pageNo, pageSize: 20 },
        schema: InductionPassList,
      }),
    placeholderData: keepPreviousData,
  });
  return (
    <Dialog title={t('w18.ind.passesFor', { name: induction.name })} onClose={onClose} wide>
      <div className="mb-3 flex justify-end">
        <ExportButton path={`${COMPLIANCE}/inductions/report/export`} query={{ inductionId: induction.id }} fileName="induction-passes" />
      </div>
      {q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Spinner />
      ) : (
        <>
          <TableWrap caption={t('w18.ind.passesFor', { name: induction.name })}>
            <thead className="bg-slate-50">
              <tr>
                <Th>{t('w18.ind.pass.who')}</Th>
                <Th>{t('w18.ind.pass.passed')}</Th>
                <Th>{t('w18.ind.pass.expires')}</Th>
                <Th>{t('w18.ind.pass.score')}</Th>
                <Th>{t('w18.ind.pass.via')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {q.data.items.length === 0 ? <EmptyRow cols={5} /> : null}
              {q.data.items.map((p) => (
                <tr key={p.id}>
                  <td className="px-4 py-2.5">
                    {p.visitorName ?? p.memberId ?? ''}
                    <span className="ml-2 text-xs text-slate-600">{t('w18.ind.pass.version', { version: p.version })}</span>
                  </td>
                  <td className="px-4 py-2.5">{formatDateTime(p.passedAt)}</td>
                  <td className="px-4 py-2.5">{formatDateTime(p.expiresAt)}</td>
                  <td className="px-4 py-2.5">{p.scorePercent}%</td>
                  <td className="px-4 py-2.5">{t(`w18.ind.via.${p.via}`)}</td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pager page={pageNo} pageSize={20} total={q.data.total} onPage={setPageNo} />
        </>
      )}
    </Dialog>
  );
}

/** Manage -> Inductions: list, editor (media, quiz, pass mark, validity, groups and sites) and passes (contract compliance s6). */
export function InductionsPage() {
  const canManage = useCan('inductions.manage');
  const q = useQuery({
    queryKey: ['w18', 'inductions', 'list'],
    queryFn: () => api.get(`${COMPLIANCE}/inductions`, { schema: InductionList }),
  });
  const groups = useGroupOptions();
  const [editing, setEditing] = useState<Induction | 'new' | null>(null);
  const [passes, setPasses] = useState<Induction | null>(null);
  const groupName = (id: string) => groups.data?.find((g) => g.id === id)?.name ?? '';
  return (
    <div>
      <PageHeader
        title={t('w18.ind.title')}
        description={t('w18.ind.description')}
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setEditing('new')}>
              {t('w18.ind.add')}
            </Button>
          ) : undefined
        }
      />
      {q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Spinner />
      ) : (
        <TableWrap caption={t('w18.ind.title')}>
          <thead className="bg-slate-50">
            <tr>
              <Th>{t('w18.ind.name')}</Th>
              <Th>{t('w18.ind.assign')}</Th>
              <Th>{t('w18.ind.quiz')}</Th>
              <Th>{t('common.status')}</Th>
              <Th>{t('common.actions')}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {q.data.length === 0 ? <EmptyRow cols={5} /> : null}
            {q.data.map((i) => (
              <tr key={i.id}>
                <td className="px-4 py-2.5">
                  <div className="font-medium text-slate-900">{i.name}</div>
                  <div className="text-xs text-slate-600">
                    {t('w18.ind.listMeta', {
                      version: i.version,
                      months: i.validityMonths,
                      media: i.mediaKind ? t(`w18.ind.mediaKind.${i.mediaKind}`) : t('w18.ind.noMedia'),
                    })}
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  {i.assignments.length === 0 ? t('w18.ind.noAssign') : Array.from(new Set(i.assignments.map((a) => groupName(a.groupId)).filter(Boolean))).join(', ')}
                </td>
                <td className="px-4 py-2.5">
                  {t('w18.ind.quizMeta', {
                    count: i.questions.length,
                    mark: i.passMarkPercent,
                  })}
                </td>
                <td className="px-4 py-2.5">
                  <Badge tone={i.active ? 'good' : 'neutral'}>{i.active ? t('w18.types.activeBadge') : t('w18.types.inactiveBadge')}</Badge>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-wrap gap-2">
                    {canManage ? (
                      <Button onClick={() => setEditing(i)} aria-label={t('w18.ind.editName', { name: i.name })}>
                        {t('common.edit')}
                      </Button>
                    ) : null}
                    <Button onClick={() => setPasses(i)} aria-label={t('w18.ind.passesName', { name: i.name })}>
                      {t('w18.ind.passes')}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
      {!q.data && !q.isError ? null : (
        <Card className="mt-6" title={t('w18.ind.helpTitle')}>
          <p className="text-sm text-slate-700">{t('w18.ind.help')}</p>
        </Card>
      )}
      {editing ? <Editor induction={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
      {passes ? <PassesDialog induction={passes} onClose={() => setPasses(null)} /> : null}
    </div>
  );
}
